import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  fetchAllKpis,
  fetchAiChat,
  fetchDepartments,
  fetchPayerAccounts,
  fetchAvailableYears,
} from '../api/kpiApi';
import SortableTable from '../components/SortableTable';
import { getSessionData, setSessionData } from '../store/sessionDataStore';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import TruckLoader from '../components/TruckLoader';
import AppIcon from '../components/AppIcon';
import ExportMenu from '../components/ExportMenu';
import { exportKpi } from '../utils/kpiExport';
import { friendlyError } from '../utils/friendlyError';

import './SolShell.css';

const createInitialSectionLoading = () => ({
  tripsHour: true,
  onTime: true,
  completion: true,
  peakHours: true,
  safety: true,
  paidLoaded: true,
  profitDriver: true,
  profitTrip: true,
  roi: true,
  dispatcher: true,
});

/* ── helpers ── */
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const monthIndex = (name) => MONTHS.indexOf(name) + 1;
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtDollar = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CHART_COLORS = {
  primary: '#4680ff',
  success: '#2ed8b6',
  warning: '#ffb64d',
  danger: '#ff5370',
  accent: '#7759de',
  grid: '#dfe5f2',
};
const PIE_COLORS = ['#4680ff', '#2ed8b6', '#ffb64d', '#ff5370', '#7759de', '#00c0ef', '#5e72e4', '#26dad2', '#ff9f43'];

/* ── Shared SVG gradient definitions for charts ── */
const ChartGradients = () => (
  <defs>
    <linearGradient id="gradPrimary" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#4680ff" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#4680ff" stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id="gradSuccess" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#2ed8b6" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#2ed8b6" stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id="gradWarning" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#ffb64d" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#ffb64d" stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id="gradDanger" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#ff5370" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#ff5370" stopOpacity={0.1} />
    </linearGradient>
    <linearGradient id="gradAccent" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#7759de" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#7759de" stopOpacity={0.1} />
    </linearGradient>
  </defs>
);

/* ── Custom tooltip matching Poco style ── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="kpi-tooltip">
      <div className="kpi-tooltip-label">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="kpi-tooltip-row">
          <span className="kpi-tooltip-dot" style={{ background: entry.color }} />
          <span className="kpi-tooltip-name">{entry.name}:</span>
          <span className="kpi-tooltip-val">{typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}</span>
        </div>
      ))}
    </div>
  );
};
const formatDateTick = (value) => {
  if (value == null || value === '') return '';
  const raw = String(value);
  const normalized = raw.includes('T') ? raw : `${raw}T00:00:00`;
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return raw;
  return `${dt.getMonth() + 1}/${dt.getDate()}`;
};
const formatIsoDate = (value) => {
  if (!value) return '';
  const dt = value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
};
const shiftIsoDate = (value, days) => {
  const dt = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return value;
  dt.setDate(dt.getDate() + days);
  return formatIsoDate(dt);
};
const formatRangeLabel = (start, end) => {
  if (!start || !end) return '—';
  const startRaw = String(start);
  const endRaw = String(end);
  const startDisplay = startRaw.includes('-') ? startRaw : formatDateTick(startRaw);
  const endDisplay = endRaw.includes('-') ? endRaw : formatDateTick(endRaw);
  return `${startDisplay} to ${endDisplay}`;
};
const normalizeDepartmentKey = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const normalizeDepartmentLabel = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const roundTo = (value, places = 1) => {
  const factor = 10 ** places;
  return Math.round((Number(value || 0) + Number.EPSILON) * factor) / factor;
};

/* ── Section card wrapper ── */
const KpiSection = ({ title, description, children, sourceSystems, exportMenu }) => (
  <div className="chart-card" style={{ marginBottom: 24 }}>
    <div className="chart-header" style={{ marginBottom: 8 }}>
      <div>
        <div className="chart-title">{title}</div>
        <div className="chart-subtitle">{description}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {sourceSystems && <span className="chart-subtitle">{sourceSystems}</span>}
        {exportMenu}
      </div>
    </div>
    {children}
  </div>
);

/* ── Small stat box — template-style card with colored left border ── */
const STAT_VARIANTS = {
  default:  { border: '#3b82f6', color: '#1e293b', subColor: '#64748b' },
  blue:     { border: '#3b82f6', color: '#1e293b', subColor: '#3b82f6' },
  green:    { border: '#22c55e', color: '#16a34a', subColor: '#16a34a' },
  red:      { border: '#ef4444', color: '#dc2626', subColor: '#dc2626' },
  purple:   { border: '#8b5cf6', color: '#1e293b', subColor: '#7c3aed' },
  orange:   { border: '#f59e0b', color: '#1e293b', subColor: '#d97706' },
  teal:     { border: '#14b8a6', color: '#1e293b', subColor: '#0d9488' },
};
const StatBox = ({ label, value, sub, variant = 'default' }) => {
  const v = STAT_VARIANTS[variant] || STAT_VARIANTS.default;
  return (
    <div className="kpi-stat-card" style={{ borderLeftColor: v.border }}>
      <div className="kpi-stat-label">{label}</div>
      <div className="kpi-stat-value" style={{ color: v.color }}>{value}</div>
      {sub && <div className="kpi-stat-sub" style={{ color: v.subColor }}>{sub}</div>}
    </div>
  );
};

const ChartModeSelector = ({ value, options, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', fontWeight: 600 }}>
      <span>Chart Type</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          minWidth: 150,
          padding: '6px 32px 6px 10px',
          borderRadius: 8,
          border: '1px solid #cbd5e1',
          background: '#fff',
          color: '#0f172a',
          fontSize: 12,
          fontWeight: 500,
          outline: 'none',
          cursor: 'pointer',
        }}
        aria-label="Select chart type"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  </div>
);

const KpiDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin, canUpload, getIdToken, profileLoading, profileError } = useAuth();

  const now = new Date();
  const _initYear  = now.getFullYear();
  const _initMonth = MONTHS[now.getMonth()];
  const _initRangeEnd = formatIsoDate(now);
  // Seed initial state from cache so no spinner flash on back-navigation
  // monthly period has no anchor (null → ''), must match prefetchSessionData key format
  const _ic = getSessionData(`kpi:${_initYear}:${monthIndex(_initMonth)}:monthly:`);

  const [year, setYear] = useState(String(_initYear));
  const [month, setMonth] = useState(_initMonth);
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState(shiftIsoDate(_initRangeEnd, -29));
  const [endDate, setEndDate] = useState(_initRangeEnd);
  const numYear = Number(year);
  const numMonth = monthIndex(month);

  /* state for each KPI — pre-seeded from cache when available */
  const [tripsHour, setTripsHour] = useState(_ic?.tripsHour || null);
  const [onTime, setOnTime] = useState(_ic?.onTime || null);
  const [completion, setCompletion] = useState(_ic?.completion || null);
  const [peakHours, setPeakHours] = useState(_ic?.peakHours || null);
  const [safety, setSafety] = useState(_ic?.safety || null);
  const [paidLoaded, setPaidLoaded] = useState(_ic?.paidLoaded || null);
  const [profitDriver, setProfitDriver] = useState(_ic?.profitDriver || null);
  const [profitTrip, setProfitTrip] = useState(_ic?.profitTrip || null);
  const [roi, setRoi] = useState(_ic?.roi || null);
  const [dispatcher, setDispatcher] = useState(_ic?.dispatcher || null);
  const [sectionLoading, setSectionLoading] = useState(() =>
    _ic
      ? { tripsHour: false, onTime: false, completion: false, peakHours: false, safety: false, paidLoaded: false, profitDriver: false, profitTrip: false, roi: false, dispatcher: false }
      : createInitialSectionLoading()
  );
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [payerAccounts, setPayerAccounts] = useState([]);
  const [selectedPayerId, setSelectedPayerId] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [chartModes, setChartModes] = useState({
    kpi1: 'bar',
    kpi2: 'area',
    kpi4Hourly: 'bar',
    kpi6: 'bar',
  });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([
    {
      role: 'assistant',
      text: 'Hi, I am SOL AI Assistant. Ask me about KPI trends, payer performance, or operational focus areas.',
    },
  ]);
  const aiMessagesRef = useRef(null);
  const setChartMode = (kpi, mode) => setChartModes((current) => ({ ...current, [kpi]: mode }));
  const latestRequestRef = useRef(0);
  const selectedDeptKey = useMemo(() => normalizeDepartmentKey(selectedDept), [selectedDept]);
  const driverSearchKey = useMemo(() => String(driverSearch || '').trim().toLowerCase(), [driverSearch]);

  const matchesDepartment = useCallback((department) => {
    if (!selectedDeptKey) return true;
    return normalizeDepartmentKey(department) === selectedDeptKey;
  }, [selectedDeptKey]);

  const matchesDriverSearch = useCallback((name) => {
    if (!driverSearchKey) return true;
    return String(name || '').toLowerCase().includes(driverSearchKey);
  }, [driverSearchKey]);

  // Fetch available years + departments once — runs when user auth is confirmed ready
  useEffect(() => {
    if (!user) return;
    fetchAvailableYears()
      .then((r) => setAvailableYears(r.years || []))
      .catch(() => {});
    fetchDepartments()
      .then((r) => {
        const normalized = Array.from(new Set((r.departments || [])
          .map(normalizeDepartmentLabel)
          .filter(Boolean)));
        setDepartments(normalized);
      })
      .catch(() => {});
    fetchPayerAccounts()
      .then((r) => setPayerAccounts(r.payers || []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (period === 'custom' && (!startDate || !endDate)) {
      setStartDate(shiftIsoDate(_initRangeEnd, -29));
      setEndDate(_initRangeEnd);
    }
  }, [period, startDate, endDate, _initRangeEnd]);

  const loadAll = useCallback(async () => {
    const selectedStartDate = period === 'custom' ? startDate : null;
    const selectedEndDate = period === 'custom' ? endDate : null;
    if (period === 'custom' && selectedStartDate && selectedEndDate && selectedStartDate > selectedEndDate) {
      setError('Start date must be on or before end date.');
      setSectionLoading({
        tripsHour: false, onTime: false, completion: false, peakHours: false,
        safety: false, paidLoaded: false, profitDriver: false, profitTrip: false,
        roi: false, dispatcher: false,
      });
      return;
    }
    const cacheKey = period === 'monthly'
      ? `kpi:${numYear}:${numMonth}:monthly::${selectedDept}:${selectedPayerId || ''}`
      : `kpi:custom:${selectedStartDate || ''}:${selectedEndDate || ''}:${selectedDept}:${selectedPayerId || ''}`;
    console.log(`[kpi] loadAll — key: "${cacheKey}"`);
    const cached = getSessionData(cacheKey);
    if (cached) {
      const age = cached.cachedAt ? Math.round((Date.now() - cached.cachedAt) / 1000) : '?';
      console.log(`[kpi] CACHE HIT (${age}s old) — rendering instantly`);
      setTripsHour(cached.tripsHour || null);
      setOnTime(cached.onTime || null);
      setCompletion(cached.completion || null);
      setPeakHours(cached.peakHours || null);
      setSafety(cached.safety || null);
      setPaidLoaded(cached.paidLoaded || null);
      setProfitDriver(cached.profitDriver || null);
      setProfitTrip(cached.profitTrip || null);
      setRoi(cached.roi || null);
      setDispatcher(cached.dispatcher || null);
      setError('');
      setSectionLoading({
        tripsHour: false, onTime: false, completion: false, peakHours: false,
        safety: false, paidLoaded: false, profitDriver: false, profitTrip: false,
        roi: false, dispatcher: false,
      });
      return;
    }

    console.log('[kpi] CACHE MISS — calling /api/kpi/all');
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    setError('');
    setSectionLoading(createInitialSectionLoading());

    const t0 = performance.now();
    try {
      const all = await fetchAllKpis(
        period === 'monthly' ? numYear : null,
        period === 'monthly' ? numMonth : null,
        period === 'custom' ? 'weekly' : 'monthly',
        null, selectedStartDate, selectedEndDate, selectedDept || null, selectedPayerId || null
      );
      console.log(`[kpi] /api/kpi/all fetch: ${Math.round(performance.now() - t0)}ms`);
      if (latestRequestRef.current !== requestId) return;

      setTripsHour(all.tripsHour || null);
      setOnTime(all.onTime || null);
      setCompletion(all.completion || null);
      setPeakHours(all.peakHours || null);
      setSafety(all.safety || null);
      setPaidLoaded(all.paidLoaded || null);
      setProfitDriver(all.profitDriver || null);
      setProfitTrip(all.profitTrip || null);
      setRoi(all.roi || null);
      setDispatcher(all.dispatcher || null);
      setSectionLoading({
        tripsHour: false, onTime: false, completion: false, peakHours: false,
        safety: false, paidLoaded: false, profitDriver: false, profitTrip: false,
        roi: false, dispatcher: false,
      });
      setSessionData(cacheKey, { ...all, cachedAt: Date.now() });
    } catch (err) {
      if (latestRequestRef.current !== requestId) return;
      setSectionLoading({
        tripsHour: false, onTime: false, completion: false, peakHours: false,
        safety: false, paidLoaded: false, profitDriver: false, profitTrip: false,
        roi: false, dispatcher: false,
      });
      setError(friendlyError(err, 'Failed to load dashboard data.'));
    }
  }, [numYear, numMonth, period, startDate, endDate, selectedDept, selectedPayerId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* user display */
  const displayName = useMemo(() => user?.displayName || user?.email || 'User', [user]);
  const avatar = useMemo(() => {
    const b = (user?.displayName || user?.email || 'U').trim();
    const p = b.split(/\s+/).filter(Boolean);
    return (p.length >= 2 ? `${p[0][0]}${p[1][0]}` : `${b[0]}`).toUpperCase();
  }, [user]);
  const handleLogout = async () => { await signOut(); navigate('/', { replace: true }); };

  /* completion pie data */
  const completionPie = completion?.status_breakdown?.map((s, i) => ({
    name: s.status, value: s.count, fill: PIE_COLORS[i % PIE_COLORS.length],
  })) || [];

  /* Single data-freshness note extracted from the first KPI that has coverage info */
  const dataCoverageNote = useMemo(() => {
    const sources = [tripsHour, onTime, completion, peakHours, safety, paidLoaded, profitDriver, profitTrip, roi, dispatcher];
    for (const src of sources) {
      if (src?.data_coverage?.is_partial && src?.data_coverage?.note) {
        return src.data_coverage.note;
      }
    }
    return null;
  }, [tripsHour, onTime, completion, peakHours, safety, paidLoaded, profitDriver, profitTrip, roi, dispatcher]);

  const paidLoadedTrend = useMemo(() => {
    return (paidLoaded?.daily_trend || [])
      .map((row) => ({ ...row, date: row?.date == null ? '' : String(row.date) }))
      .filter((row) => row.date !== '');
  }, [paidLoaded]);

  const appliedRange = useMemo(() => {
    const sources = [tripsHour, onTime, completion, peakHours, safety, paidLoaded, profitDriver, profitTrip, roi, dispatcher];
    for (const src of sources) {
      if (src?.start_date && src?.end_date) {
        return { start: src.start_date, end: src.end_date };
      }
    }
    return null;
  }, [tripsHour, onTime, completion, peakHours, safety, paidLoaded, profitDriver, profitTrip, roi, dispatcher]);

  const periodLabel = useMemo(() => {
    if (period === 'monthly') return `${month} ${year}`;
    if (appliedRange) return formatRangeLabel(appliedRange.start, appliedRange.end);
    return formatRangeLabel(startDate, endDate);
  }, [period, month, year, appliedRange, startDate, endDate]);

  const buildAiReply = useCallback((questionRaw) => {
    const q = String(questionRaw || '').trim().toLowerCase();
    const quickFacts = [];

    if (tripsHour?.trips_per_driver_hour != null) {
      quickFacts.push(`Trips per driver-hour is ${tripsHour.trips_per_driver_hour}.`);
    }
    if (onTime?.on_time_rate_pct != null) {
      quickFacts.push(`On-time pickup rate is ${onTime.on_time_rate_pct}%.`);
    }
    if (completion?.completion_rate_pct != null) {
      quickFacts.push(`Completion rate is ${completion.completion_rate_pct}%.`);
    }
    if (profitTrip?.profit_per_trip != null) {
      quickFacts.push(`Profit per trip is ${fmtDollar(profitTrip.profit_per_trip)}.`);
    }

    if (q.includes('payer') || q.includes('account')) {
      if (selectedPayerId) {
        return `You are currently filtered to payer account ${selectedPayerId}. ${quickFacts.slice(0, 2).join(' ')} Keep monitoring on-time and completion together for this payer to reduce service risk.`;
      }
      return 'No payer filter is selected right now. Choose a payer account in filters to get account-specific KPI movements and comparisons.';
    }

    if (q.includes('on-time') || q.includes('late')) {
      if (onTime?.on_time_rate_pct == null) return 'On-time KPI is still loading. Please try again in a moment.';
      const rate = Number(onTime.on_time_rate_pct || 0);
      if (rate >= 90) return `On-time is healthy at ${rate}%. Focus next on sustaining completion consistency and dispatcher throughput.`;
      if (rate >= 80) return `On-time is moderate at ${rate}%. Suggested action: identify top delay routes and tighten dispatch-driver handoff windows.`;
      return `On-time is low at ${rate}%. Priority: route-level root cause review and dispatcher intervention during peak hours.`;
    }

    if (q.includes('profit') || q.includes('margin') || q.includes('roi')) {
      const pTrip = profitTrip?.profit_per_trip != null ? fmtDollar(profitTrip.profit_per_trip) : 'N/A';
      const roiPct = roi?.roi_pct != null ? `${roi.roi_pct}%` : 'N/A';
      return `Profit snapshot: profit per trip ${pTrip}, overall ROI ${roiPct}. Focus on reducing empty/idle time and fuel-cost hotspots to improve margin.`;
    }

    if (q.includes('summary') || q.includes('insight') || q.includes('overall')) {
      if (!quickFacts.length) return 'Dashboard KPI data is still loading, so I cannot summarize yet. Please retry once data appears.';
      return `Current KPI summary: ${quickFacts.slice(0, 4).join(' ')} Recommended next step: drill into the weakest KPI and review by department and payer filter.`;
    }

    return `I can help with KPI summaries, payer-specific trends, on-time analysis, and profitability actions. ${quickFacts.slice(0, 2).join(' ')}`;
  }, [selectedPayerId, tripsHour, onTime, completion, profitTrip, roi]);

  const sendAiMessage = useCallback(async () => {
    const text = String(aiInput || '').trim();
    if (!text || aiBusy) return;
    const nextMessages = [...aiMessages, { role: 'user', text }];
    setAiMessages(nextMessages);
    setAiInput('');
    setAiBusy(true);
    try {
      const aiContext = {
        period_label: periodLabel,
        department: selectedDept || 'all',
        payer_account_id: selectedPayerId || 'all',
        trips_per_driver_hour: tripsHour?.trips_per_driver_hour != null ? String(tripsHour.trips_per_driver_hour) : '',
        on_time_pickup_rate_pct: onTime?.on_time_rate_pct != null ? String(onTime.on_time_rate_pct) : '',
        completion_rate_pct: completion?.completion_rate_pct != null ? String(completion.completion_rate_pct) : '',
        profit_per_trip: profitTrip?.profit_per_trip != null ? String(profitTrip.profit_per_trip) : '',
        overall_roi_pct: roi?.roi_pct != null ? String(roi.roi_pct) : '',
      };
      const res = await fetchAiChat(
        nextMessages.map((m) => ({ role: m.role, text: m.text })),
        aiContext
      );
      const reply = String(res?.reply || '').trim() || buildAiReply(text);
      setAiMessages((prev) => [...prev, { role: 'assistant', text: reply }]);
    } catch (err) {
      const fallback = buildAiReply(text);
      setAiMessages((prev) => [...prev, { role: 'assistant', text: fallback }]);
    }
    setAiBusy(false);
  }, [
    aiInput,
    aiBusy,
    aiMessages,
    buildAiReply,
    periodLabel,
    selectedDept,
    selectedPayerId,
    tripsHour,
    onTime,
    completion,
    profitTrip,
    roi,
  ]);

  useEffect(() => {
    if (!aiOpen) return;
    const node = aiMessagesRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [aiMessages, aiOpen]);

  const displayedTripsHour = useMemo(() => {
    if (!tripsHour || !selectedDeptKey) return tripsHour;
    const byDriver = (tripsHour.by_driver || []).filter((row) => matchesDepartment(row.department));
    const byDepartment = (tripsHour.by_department || []).filter((row) => matchesDepartment(row.department));
    const completedTrips = byDriver.reduce((sum, row) => sum + Number(row?.trips || 0), 0);
    const totalPaidHours = byDriver.reduce((sum, row) => sum + Number(row?.paid_hours || 0), 0);
    return {
      ...tripsHour,
      completed_trips: completedTrips,
      total_paid_hours: roundTo(totalPaidHours, 1),
      total_drivers: byDriver.length,
      trips_per_driver_hour: totalPaidHours > 0 ? roundTo(completedTrips / totalPaidHours, 2) : 0,
      by_driver: byDriver,
      by_department: byDepartment,
    };
  }, [tripsHour, selectedDeptKey, matchesDepartment]);

  const displayedProfitDriver = useMemo(() => {
    if (!profitDriver || !selectedDeptKey) return profitDriver;
    const drivers = (profitDriver.drivers || []).filter((row) => matchesDepartment(row.department));
    const totalLaborCost = drivers.reduce((sum, row) => sum + Number(row?.labor_cost || 0), 0);
    const totalFuelCost = drivers.reduce((sum, row) => sum + Number(row?.fuel_cost || 0), 0);
    const totalCost = drivers.reduce((sum, row) => sum + Number(row?.total_cost || 0), 0);
    const totalRevenue = drivers.reduce((sum, row) => sum + Number(row?.revenue || 0), 0);
    const totalProfit = drivers.reduce((sum, row) => sum + Number(row?.profit || 0), 0);
    return {
      ...profitDriver,
      total_labor_cost: roundTo(totalLaborCost, 2),
      total_fuel_cost: roundTo(totalFuelCost, 2),
      total_cost: roundTo(totalCost, 2),
      total_revenue: roundTo(totalRevenue, 2),
      total_profit: roundTo(totalProfit, 2),
      overall_roi_pct: totalCost > 0 ? roundTo((totalProfit / totalCost) * 100, 1) : 0,
      overall_margin_pct: totalRevenue > 0 ? roundTo((totalProfit / totalRevenue) * 100, 1) : 0,
      driver_count: drivers.length,
      drivers,
    };
  }, [profitDriver, selectedDeptKey, matchesDepartment]);

  /* ── Export sections data ── */
  const kpiSections = useMemo(() => {
    const p = periodLabel;
    return [
      {
        title: '1. Trips per Driver Hour',
        period: p,
        chartId: 'kpi-chart-1',
        stats: displayedTripsHour ? [
          { label: 'Completed Trips', value: fmtNum(displayedTripsHour.completed_trips) },
          { label: 'Total Paid Hours', value: fmtNum(displayedTripsHour.total_paid_hours) },
          { label: 'Trips / Driver-Hour', value: displayedTripsHour.trips_per_driver_hour ?? '—' },
          { label: 'Total Drivers', value: fmtNum(displayedTripsHour.total_drivers) },
        ] : [],
        headers: displayedTripsHour ? ['Driver', 'Department', 'Trips', 'Paid Hours', 'Trips/Hr'] : undefined,
        rows: (() => {
          const driverRows = (displayedTripsHour?.by_driver || [])
            .filter(d => matchesDepartment(d.department))
            .map(d => [d.driver_name, d.department || '', d.trips, d.paid_hours, d.trips_per_hour]);
          const deptHeader = displayedTripsHour?.by_department?.length > 0 ? [[], ['Department', '', 'Drivers', 'Paid Hours', 'Trips/Hr']] : [];
          const deptRows = (displayedTripsHour?.by_department || [])
            .filter(d => matchesDepartment(d.department))
            .map(d => [d.department || '(none)', '', d.drivers, d.paid_hours, d.trips_per_hour]);
          return [...driverRows, ...deptHeader, ...deptRows];
        })(),
      },
      {
        title: '2. On-Time Pickup Rate',
        period: p,
        chartId: 'kpi-chart-2',
        stats: onTime ? [
          { label: 'On-Time Rate', value: `${onTime.on_time_rate_pct}%` },
          { label: 'On Time', value: fmtNum(onTime.on_time) },
          { label: 'Late', value: fmtNum(onTime.late) },
          { label: 'Avg Delay', value: `${onTime.avg_delay_minutes} min` },
          { label: 'Max Delay', value: `${onTime.max_delay_minutes} min` },
        ] : [],
        headers: onTime?.daily_trend?.length > 0 ? ['Date', 'On-Time %', 'On Time', 'Late'] : undefined,
        rows: onTime?.daily_trend?.map(d => [d.date, `${d.rate_pct}%`, d.on_time, d.late]) || [],
      },
      {
        title: '3. Scheduled Trips Completion Status',
        period: p,
        chartId: 'kpi-chart-3',
        stats: completion ? [
          { label: 'Total Trips', value: fmtNum(completion.total_trips) },
          { label: 'Completed', value: `${fmtNum(completion.completed)} (${completion.completion_pct}%)` },
          { label: 'Canceled', value: `${fmtNum(completion.canceled)} (${completion.cancellation_pct}%)` },
          { label: 'No Show', value: fmtNum(completion.no_show) },
          { label: 'In Route', value: fmtNum(completion.in_route) },
        ] : [],
        headers: completion?.status_breakdown?.length > 0 ? ['Status', 'Count', 'Percent %'] : undefined,
        rows: (() => {
          const breakdown = (completion?.status_breakdown || []).map(s => {
            const pct = completion.total_trips > 0 ? ((s.count / completion.total_trips) * 100).toFixed(1) : '0.0';
            return [s.status, s.count, `${pct}%`];
          });
          const dailyHeader = completion?.daily_trend?.length > 0 ? [[], ['Date', 'Total', 'Completed', 'Canceled', 'No Show', 'In Route']] : [];
          const dailyRows = (completion?.daily_trend || []).map(d => [d.date, d.total, d.completed, d.canceled, d.no_show, d.in_route]);
          return [...breakdown, ...dailyHeader, ...dailyRows];
        })(),
      },
      {
        title: '4. Business Output During Peak Hours',
        period: p,
        chartId: 'kpi-chart-4',
        stats: peakHours ? [
          { label: 'Peak Hour', value: peakHours.peak_hour != null ? `${String(peakHours.peak_hour).padStart(2, '0')}:00` : '—' },
          { label: 'Peak Day', value: peakHours.peak_day || '—' },
        ] : [],
        headers: peakHours?.hourly_distribution?.length > 0 ? ['Hour', 'Total Trips', 'Completed'] : undefined,
        rows: (() => {
          const hourly = (peakHours?.hourly_distribution || []).map(h => [h.label, h.trips, h.completed]);
          const dayHeader = peakHours?.daily_distribution?.length > 0 ? [[], ['Day of Week', 'Total Trips', 'Completed']] : [];
          const dayRows = (peakHours?.daily_distribution || []).map(h => [h.day_name, h.trips, h.completed]);
          const shiftHeader = peakHours?.shift_distribution?.length > 0 ? [[], ['Shift Start Hour', 'Punches', 'Drivers']] : [];
          const shiftRows = (peakHours?.shift_distribution || []).map(h => [h.label, h.punches, h.drivers]);
          return [...hourly, ...dayHeader, ...dayRows, ...shiftHeader, ...shiftRows];
        })(),
      },
      {
        title: '5. Safe Driver Analysis',
        period: p,
        stats: safety?.data_available ? [
          { label: 'Fleet Avg Score', value: safety.fleet_avg_safety_score ?? '—' },
          { label: 'Drivers Scored', value: fmtNum(safety.driver_count) },
        ] : [],
        headers: safety?.drivers?.length > 0 ? ['Driver', 'Safety Score', 'Harsh Events', 'Braking', 'Accel', 'Turning', 'Crashes', 'Speeding (min)'] : undefined,
        rows: safety?.drivers?.map(d => [d.driver_name, d.avg_safety_score, d.harsh_events, d.harsh_braking, d.harsh_accel, d.harsh_turning, d.crashes, d.speeding_minutes]) || [],
      },
      {
        title: '6. Paid vs Loaded / Empty Time Utilization',
        period: p,
        chartId: 'kpi-chart-6',
        stats: paidLoaded ? [
          { label: 'Paid Hours', value: `${fmtNum(paidLoaded.paid_hours)} (${paidLoaded.employee_count} emp)` },
          { label: 'Active Hours', value: fmtNum(paidLoaded.active_hours) },
          { label: 'Idle Hours', value: fmtNum(paidLoaded.idle_hours) },
          {
            label: 'Utilization',
            value: paidLoaded.raw_utilization_pct > 100
              ? `${paidLoaded.utilization_pct}% ⚠️`
              : `${paidLoaded.utilization_pct}%`,
            note: paidLoaded.data_gap_warning || undefined,
          },
          { label: 'Total Miles', value: fmtNum(paidLoaded.total_miles) },
        ] : [],
        headers: paidLoaded?.daily_trend?.length > 0 ? ['Date', 'Paid Hours', 'Active Hours', 'Idle Hours'] : undefined,
        rows: paidLoaded?.daily_trend?.map(d => [d.date, d.paid, d.active, d.idle]) || [],
      },
      {
        title: '7. Profit per Driver',
        period: p,
        stats: displayedProfitDriver ? [
          { label: 'Labor Cost', value: fmtDollar(displayedProfitDriver.total_labor_cost) },
          { label: 'Fuel Cost', value: fmtDollar(displayedProfitDriver.total_fuel_cost) },
          { label: 'Total Cost', value: fmtDollar(displayedProfitDriver.total_cost) },
          { label: 'Total Revenue', value: fmtDollar(displayedProfitDriver.total_revenue) },
          { label: 'Total Profit', value: fmtDollar(displayedProfitDriver.total_profit) },
          { label: 'Overall Margin', value: displayedProfitDriver.overall_margin_pct != null ? `${displayedProfitDriver.overall_margin_pct}%` : '—' },
          { label: 'Overall ROI', value: displayedProfitDriver.overall_roi_pct != null ? `${displayedProfitDriver.overall_roi_pct}%` : '—' },
          { label: 'Drivers', value: fmtNum(displayedProfitDriver.driver_count) },
        ] : [],
        headers: displayedProfitDriver?.drivers?.length > 0 ? ['Driver', 'Dept', 'Paid Hrs', 'Rate', 'Labor Cost', 'Fuel Cost', 'Total Cost', 'Revenue', 'Profit', 'Margin %', 'ROI %'] : undefined,
        rows: (displayedProfitDriver?.drivers || [])
          .filter(d => matchesDepartment(d.department))
          .map(d => [d.name, d.department, d.paid_hours, fmtDollar(d.base_rate), fmtDollar(d.labor_cost), fmtDollar(d.fuel_cost), fmtDollar(d.total_cost), fmtDollar(d.revenue), fmtDollar(d.profit), d.profit_margin_pct != null ? `${d.profit_margin_pct}%` : '—', d.roi_pct != null ? `${d.roi_pct}%` : '—']),
      },
      {
        title: '8. Profit per Trip',
        period: p,
        stats: profitTrip ? [
          { label: 'Completed Trips', value: fmtNum(profitTrip.completed_trips) },
          { label: 'Total Revenue', value: fmtDollar(profitTrip.total_revenue) },
          { label: 'Total Cost', value: fmtDollar(profitTrip.total_cost) },
          { label: 'Total Profit', value: fmtDollar(profitTrip.total_profit) },
          { label: 'Margin %', value: profitTrip.profit_margin_pct != null ? `${profitTrip.profit_margin_pct}%` : '—' },
          { label: 'ROI %', value: profitTrip.roi_pct != null ? `${profitTrip.roi_pct}%` : '—' },
          { label: 'Revenue / Trip', value: fmtDollar(profitTrip.revenue_per_trip) },
          { label: 'Cost per Trip', value: fmtDollar(profitTrip.cost_per_trip) },
          { label: 'Profit / Trip', value: fmtDollar(profitTrip.profit_per_trip) },
          { label: 'Labor / Trip', value: fmtDollar(profitTrip.labor_per_trip) },
          { label: 'Fuel / Trip', value: fmtDollar(profitTrip.fuel_per_trip) },
        ] : [],
        headers: profitTrip ? ['Metric', 'Total', 'Per Trip'] : undefined,
        rows: profitTrip ? [
          ['Completed Trips', profitTrip.completed_trips, '—'],
          ['Revenue', fmtDollar(profitTrip.total_revenue), fmtDollar(profitTrip.revenue_per_trip)],
          ['Labor Cost', fmtDollar(profitTrip.labor_cost), fmtDollar(profitTrip.labor_per_trip)],
          ['Fuel Cost', fmtDollar(profitTrip.fuel_cost), fmtDollar(profitTrip.fuel_per_trip)],
          ['Total Cost', fmtDollar(profitTrip.total_cost), fmtDollar(profitTrip.cost_per_trip)],
          ['Gross Profit', fmtDollar(profitTrip.total_profit), fmtDollar(profitTrip.profit_per_trip)],
          ['Profit Margin %', profitTrip.profit_margin_pct != null ? `${profitTrip.profit_margin_pct}%` : '—', '—'],
          ['ROI %', profitTrip.roi_pct != null ? `${profitTrip.roi_pct}%` : '—', '—'],
        ] : [],
      },
      {
        title: '9. Overall ROI (Return on Investment)',
        period: p,
        stats: roi ? [
          { label: 'Total Cost', value: fmtDollar(roi.total_cost) },
          { label: 'Labor Cost', value: `${fmtDollar(roi.labor_cost)} (${roi.labor_pct}%)` },
          { label: 'Fuel Cost', value: `${fmtDollar(roi.fuel_cost)} (${roi.fuel_pct}%)` },
          { label: 'Cost / Trip', value: fmtDollar(roi.cost_per_trip) },
          { label: 'Cost / Mile', value: fmtDollar(roi.cost_per_mile) },
          { label: 'Completed Trips', value: `${fmtNum(roi.completed_trips)} of ${fmtNum(roi.total_trips)}` },
          { label: 'Total Miles', value: fmtNum(roi.total_miles) },
          { label: 'Paid Hours', value: `${fmtNum(roi.paid_hours)} (OT: ${fmtNum(roi.ot_hours)}h)` },
          { label: 'Employees', value: fmtNum(roi.employees) },
          { label: 'Fuel Gallons', value: fmtNum(roi.gallons) },
        ] : [],
        headers: roi ? ['Metric', 'Value'] : undefined,
        rows: roi ? [
          ['Total Revenue', fmtDollar(roi.total_revenue)],
          ['Labor Cost', fmtDollar(roi.labor_cost)],
          ['Fuel Cost', fmtDollar(roi.fuel_cost)],
          ['Total Cost', fmtDollar(roi.total_cost)],
          ['Gross Profit', fmtDollar(roi.gross_profit)],
          ['Profit Margin %', roi.profit_margin_pct != null ? `${roi.profit_margin_pct}%` : '—'],
          ['ROI %', roi.roi_pct != null ? `${roi.roi_pct}%` : '—'],
          ['Labor % of Cost', roi.labor_pct != null ? `${roi.labor_pct}%` : '—'],
          ['Fuel % of Cost', roi.fuel_pct != null ? `${roi.fuel_pct}%` : '—'],
          ['Completed Trips', fmtNum(roi.completed_trips)],
          ['Total Trips', fmtNum(roi.total_trips)],
          ['Revenue / Trip', fmtDollar(roi.revenue_per_trip)],
          ['Cost / Trip', fmtDollar(roi.cost_per_trip)],
          ['Profit / Trip', fmtDollar(roi.profit_per_trip)],
          ['Total Miles', fmtNum(roi.total_miles)],
          ['Revenue / Mile', fmtDollar(roi.revenue_per_mile)],
          ['Cost / Mile', fmtDollar(roi.cost_per_mile)],
          ['Profit / Mile', fmtDollar(roi.profit_per_mile)],
          ['Paid Hours', fmtNum(roi.paid_hours)],
          ['OT Hours', fmtNum(roi.ot_hours)],
          ['Employees', fmtNum(roi.employees)],
          ['Fuel Gallons', fmtNum(roi.gallons)],
        ] : [],
      },
      {
        title: '10. Dispatcher Activity',
        period: p,
        stats: dispatcher ? [
          { label: 'Dispatchers', value: fmtNum(dispatcher.dispatcher_count) },
          { label: 'Dispatch Hours', value: fmtNum(dispatcher.total_dispatch_hours) },
          { label: 'Trips Managed', value: fmtNum(dispatcher.total_trips_managed) },
          { label: 'Trips / Dispatch Hr', value: dispatcher.trips_per_dispatch_hour ?? '—' },
          { label: 'Completed', value: fmtNum(dispatcher.completed_trips) },
          { label: 'Canceled', value: fmtNum(dispatcher.canceled_trips) },
        ] : [],
        headers: dispatcher?.dispatchers?.length > 0 ? ['Dispatcher', 'Paid Hours', 'Regular Hours', 'OT Hours', 'Shifts'] : undefined,
        rows: dispatcher?.dispatchers?.map(d => [d.name, d.paid_hours, d.regular_hours, d.ot_hours, d.shifts]) || [],
      },
    ];
  }, [displayedTripsHour, onTime, completion, peakHours, safety, paidLoaded, displayedProfitDriver, profitTrip, roi, dispatcher, periodLabel, matchesDepartment]);

  /* helper: per-section export menu */
  const secMenu = (idx, slug) => (
    <ExportMenu
      size="sm"
      label="Export"
      onExcel={() => exportKpi('excel', [kpiSections[idx]], slug, getIdToken)}
      onPdf={() => exportKpi('pdf', [kpiSections[idx]], slug, getIdToken)}
      onWord={() => exportKpi('word', [kpiSections[idx]], slug, getIdToken)}
    />
  );

  const tripsHourChartData = useMemo(() => {
    return (displayedTripsHour?.by_driver || [])
      .filter((row) => Number(row?.trips || 0) > 0)
      .sort((a, b) => Number(b?.trips || 0) - Number(a?.trips || 0))
      .slice(0, 20);
  }, [displayedTripsHour]);

  const loadingPlaceholder = (
    <div style={{ padding: '20px 0' }}>
      <div className="kpi-skeleton-row" style={{ height: 16, width: '55%', marginBottom: 10 }} />
      <div className="kpi-skeleton-row" style={{ height: 12, width: '35%', marginBottom: 18 }} />
      <div className="kpi-skeleton-row" style={{ height: 72, animationDelay: '0.1s' }} />
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div className="kpi-skeleton-row" style={{ flex: 1, height: 56, animationDelay: '0.05s' }} />
        <div className="kpi-skeleton-row" style={{ flex: 1, height: 56, animationDelay: '0.15s' }} />
        <div className="kpi-skeleton-row" style={{ flex: 1, height: 56, animationDelay: '0.1s' }} />
      </div>
    </div>
  );

  return (
    <div className="sol-shell">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="logo">
          <h1><div className="logo-icon"><AppIcon name="truck" /></div><span>SOL Transport</span></h1>
        </div>
        <div className="nav-section">
          <div className="nav-section-title">Main Menu</div>
          <Link className="nav-item active" to="/kpi-dashboard"><span className="nav-item-icon"><AppIcon name="dashboard" /></span>Dashboard</Link>
        </div>
        <div className="nav-section">
          <div className="nav-section-title">Administration</div>
          {isAdmin && <Link className="nav-item" to="/user-management"><span className="nav-item-icon"><AppIcon name="users" /></span>User Management</Link>}
          {isAdmin && <Link className="nav-item" to="/source-config"><span className="nav-item-icon"><AppIcon name="link" /></span>Source Config</Link>}
          {isAdmin && <Link className="nav-item" to="/activity-log"><span className="nav-item-icon"><AppIcon name="activity" /></span>User Activity Log</Link>}
          {canUpload && <Link className="nav-item" to="/upload"><span className="nav-item-icon"><AppIcon name="upload" /></span>Upload Files</Link>}
          {!isAdmin && !canUpload && (
            <div style={{ color: '#cbd5e1', fontSize: 12, padding: '8px 8px 0' }}>
              {profileLoading ? 'Loading permissions...' : (profileError ? 'Permissions unavailable. Please re-login.' : 'No admin/upload permissions on this account.')}
            </div>
          )}
        </div>
        <div className="sidebar-user">
          <div className="user-avatar">{avatar}</div>
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">{isAdmin ? 'Admin' : 'Verified'}</div>
          </div>
          <button className="logout-btn" type="button" onClick={handleLogout} aria-label="Logout">
            <AppIcon name="login" size={14} />Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          type="button" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <AppIcon name="menu" size={18} />
        </button>

        <div className="sticky-top-area">
          <header className="header">
            <div className="header-left">
              <h2 className="title-with-icon"><AppIcon name="kpi" /> KPI Dashboard</h2>
              <p>Key Performance Indicators — 10 required metrics</p>
            </div>
            <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ExportMenu
                label="Export All"
                onExcel={() => exportKpi('excel', kpiSections, `KPI_Dashboard_${month}_${year}`)}
                onPdf={() => exportKpi('pdf', kpiSections, `KPI_Dashboard_${month}_${year}`)}
                onWord={() => exportKpi('word', kpiSections, `KPI_Dashboard_${month}_${year}`)}
              />
            </div>
          </header>

          {dataCoverageNote && (
            <div className="data-freshness-banner">
              <AppIcon name="sync" size={14} />
              <span>{dataCoverageNote}</span>
            </div>
          )}

          {error && (
            <div style={{ padding: 12, background: '#ffebee', color: '#b00020', borderRadius: 8 }}>{error}</div>
          )}

          {/* Filters */}
          <div className="filters-bar">
            <div className="filter-group">
              <span className="filter-label">Period:</span>
              <select className="filter-select" value={period} onChange={e => setPeriod(e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>
            {period === 'monthly' ? (
              <>
                <div className="filter-group">
                  <span className="filter-label">Year:</span>
                  <select className="filter-select" value={year} onChange={e => setYear(e.target.value)}>
                    {(availableYears.length ? availableYears : [new Date().getFullYear()]).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <span className="filter-label">Month:</span>
                  <select className="filter-select" value={month} onChange={e => setMonth(e.target.value)}>
                    {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="filter-group">
                  <span className="filter-label">Start Date:</span>
                  <input
                    className="filter-select"
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    max={endDate || undefined}
                  />
                </div>
                <div className="filter-group">
                  <span className="filter-label">End Date:</span>
                  <input
                    className="filter-select"
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    min={startDate || undefined}
                  />
                </div>
              </>
            )}
            <div className="filter-group">
              <span className="filter-label">Department:</span>
              <select className="filter-select" value={selectedDept} onChange={e => setSelectedDept(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <span className="filter-label">Payer Account:</span>
              <select className="filter-select" value={selectedPayerId} onChange={e => setSelectedPayerId(e.target.value)}>
                <option value="">All Payers</option>
                {payerAccounts.map((p) => (
                  <option key={`${p.payer_account_id}:${p.payer_name}`} value={p.payer_account_id}>
                    {p.payer_account_id} - {p.payer_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <span className="filter-label">Driver Search:</span>
              <input
                className="filter-select"
                type="text"
                value={driverSearch}
                onChange={e => setDriverSearch(e.target.value)}
                placeholder="Type driver name"
              />
            </div>
          </div>
          {period === 'custom' && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
              Applied range: {periodLabel}
            </div>
          )}
        </div>

        {/* ================================================================
            KPI SECTIONS
           ================================================================ */}

        {Object.values(sectionLoading).some(Boolean) ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 420, padding: '60px 0' }}>
            <TruckLoader message="Loading KPI data…" size="md" />
          </div>
        ) : (
        <>
        {/* ── 1. Trips per Driver per Hour ── */}
        <KpiSection title="1. Trips per Driver per Hour" description="Driver productivity: completed trips ÷ paid driver-hours" sourceSystems="Source: RouteGenie + Paylocity" exportMenu={secMenu(0, '1_Trips_per_Driver_Hour')}>
          {sectionLoading.tripsHour ? loadingPlaceholder : displayedTripsHour ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Completed Trips" value={fmtNum(displayedTripsHour.completed_trips)} variant="blue" />
                <StatBox label="Total Paid Hours" value={fmtNum(displayedTripsHour.total_paid_hours)} variant="purple" />
                <StatBox label="Trips / Driver-Hour" value={displayedTripsHour.trips_per_driver_hour} variant="green" sub="Productivity ratio" />
                <StatBox label="Total Drivers" value={fmtNum(displayedTripsHour.total_drivers)} variant="teal" />
              </div>
              {tripsHourChartData.length > 0 ? (
                <>
                  <ChartModeSelector
                    value={chartModes.kpi1}
                    onChange={(mode) => setChartMode('kpi1', mode)}
                    options={[
                      { value: 'bar', label: 'Bar' },
                      { value: 'pie', label: 'Pie' },
                    ]}
                  />
                  <div id="kpi-chart-1">
                    <ResponsiveContainer width="100%" height={300}>
                      {chartModes.kpi1 === 'pie' ? (
                        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                          <Pie
                            data={tripsHourChartData.slice(0, 10).map((row) => ({ name: row.driver_name, value: Number(row.trips || 0) }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={48}
                            outerRadius={96}
                            paddingAngle={2}
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          >
                            {tripsHourChartData.slice(0, 10).map((entry, i) => <Cell key={entry.driver_name || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                        </PieChart>
                      ) : (
                        <BarChart data={tripsHourChartData} margin={{ left: 0, right: 20, bottom: 10 }}>
                          <ChartGradients />
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                          <XAxis dataKey="driver_name" tick={{ fontSize: 10, fill: '#94a3b8' }} angle={-25} textAnchor="end" height={80} interval={0} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="trips" fill="url(#gradPrimary)" stroke={CHART_COLORS.primary} strokeWidth={0} name="Completed Trips" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <div style={{ color: '#94a3b8', padding: 20 }}>
                  {displayedTripsHour?.by_driver_note || 'Driver-level trip attribution is not available for this period.'}
                </div>
              )}
              {displayedTripsHour?.by_driver?.length > 0 && (() => {
                const filteredDrivers = displayedTripsHour.by_driver.filter(
                  d => matchesDepartment(d.department) && matchesDriverSearch(d.driver_name)
                );
                return (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
                    All Drivers ({filteredDrivers.length})
                  </div>
                  <SortableTable
                    numbered
                    maxHeight={380}
                    columns={[
                      { key: 'driver_name', label: 'Driver' },
                      { key: 'department', label: 'Department' },
                      { key: 'trips', label: 'Trips', sortValue: r => Number(r.trips || 0) },
                      { key: 'paid_hours', label: 'Paid Hours', sortValue: r => Number(r.paid_hours || 0) },
                      { key: 'trips_per_hour', label: 'Trips / Hr', sortValue: r => Number(r.trips_per_hour || 0), tdStyle: () => ({ fontWeight: 600 }) },
                    ]}
                    data={filteredDrivers}
                  />
                </div>
                );
              })()}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 2. On-Time Pickup Rate ── */}
        <KpiSection title="2. On-Time Pickup Rate (%)" description="Reliability of pickups within agreed time tolerances" sourceSystems="Source: RouteGenie" exportMenu={secMenu(1, '2_OnTime_Pickup_Rate')}>
          {sectionLoading.onTime ? loadingPlaceholder : onTime ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="On-Time Rate" value={`${onTime.on_time_rate_pct}%`} variant="green" sub="Target: 90%+" />
                <StatBox label="On Time" value={fmtNum(onTime.on_time)} variant="green" />
                <StatBox label="Late" value={fmtNum(onTime.late)} variant="red" />
                <StatBox label="Avg Delay" value={`${onTime.avg_delay_minutes} min`} variant="orange" />
                <StatBox label="Max Delay" value={`${onTime.max_delay_minutes} min`} variant="red" />
              </div>
              {onTime.daily_trend?.length > 0 && (
                <div id="kpi-chart-2">
                  <ChartModeSelector
                    value={chartModes.kpi2}
                    onChange={(mode) => setChartMode('kpi2', mode)}
                    options={[
                      { value: 'area', label: 'Area' },
                      { value: 'line', label: 'Line' },
                      { value: 'bar', label: 'Bar' },
                    ]}
                  />
                  <ResponsiveContainer width="100%" height={240}>
                    {chartModes.kpi2 === 'line' ? (
                      <LineChart data={onTime.daily_trend} margin={{ left: 0, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatDateTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} unit="%" axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="rate_pct" connectNulls={false} stroke={CHART_COLORS.success} strokeWidth={2.5} name="On-Time %" dot={{ r: 3, fill: '#fff', stroke: CHART_COLORS.success, strokeWidth: 2 }} activeDot={{ r: 5, fill: CHART_COLORS.success }} />
                      </LineChart>
                    ) : chartModes.kpi2 === 'bar' ? (
                      <BarChart data={onTime.daily_trend} margin={{ left: 0, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatDateTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} unit="%" axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="rate_pct" fill={CHART_COLORS.success} name="On-Time %" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    ) : (
                      <AreaChart data={onTime.daily_trend} margin={{ left: 0, right: 20 }}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatDateTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} unit="%" axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="rate_pct" connectNulls={false} stroke={CHART_COLORS.success} strokeWidth={2.5} fill="url(#gradSuccess)" name="On-Time %" dot={{ r: 3, fill: '#fff', stroke: CHART_COLORS.success, strokeWidth: 2 }} activeDot={{ r: 5, fill: CHART_COLORS.success }} />
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                  {onTime.daily_trend?.some(d => d.rate_pct === null || d.rate_pct === undefined) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '5px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                      <span style={{ display: 'inline-block', width: 28, height: 2, borderTop: '2px dashed #92400e', verticalAlign: 'middle' }} />
                      <span><strong>Gap in line</strong> — trips exist for that day but on-time classification is not yet available in RouteGenie (field: <em>is_on_time</em> is null). Rate will appear once data is synced.</span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 3. Scheduled Trips Completion Status ── */}
        <KpiSection title="3. Scheduled Trips Completion Status" description="Daily view of scheduled trips vs completed and cancelled" sourceSystems="Source: RouteGenie" exportMenu={secMenu(2, '3_Trip_Completion_Status')}>
          {sectionLoading.completion ? loadingPlaceholder : completion ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Total Trips" value={fmtNum(completion.total_trips)} variant="blue" />
                <StatBox label="Completed" value={fmtNum(completion.completed)} sub={`${completion.completion_pct}%`} variant="green" />
                <StatBox label="Canceled" value={fmtNum(completion.canceled)} sub={`${completion.cancellation_pct}%`} variant="red" />
                <StatBox label="No Show" value={fmtNum(completion.no_show)} variant="orange" />
                <StatBox label="In Route" value={fmtNum(completion.in_route)} variant="teal" />
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {completionPie.length > 0 && (
                  <div style={{ flex: '0 0 300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <Pie
                          data={completionPie}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={46}
                          outerRadius={82}
                          paddingAngle={2}
                          labelLine={false}
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {completionPie.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {completion.daily_trend?.length > 0 && (
                  <div id="kpi-chart-3" style={{ flex: 1, minWidth: 300 }}>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={completion.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatDateTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                        <Bar dataKey="completed" stackId="a" fill={CHART_COLORS.success} name="Completed" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="canceled" stackId="a" fill={CHART_COLORS.danger} name="Canceled" />
                        <Bar dataKey="no_show" stackId="a" fill={CHART_COLORS.warning} name="No Show" />
                        <Bar dataKey="in_route" stackId="a" fill={CHART_COLORS.primary} name="In Route" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 4. Business Output During Peak Hours ── */}
        <KpiSection title="4. Business Output During Peak Hours / Peak Days" description="Productivity and throughput during peak operational periods" sourceSystems="Source: RouteGenie + Paylocity" exportMenu={secMenu(3, '4_Peak_Hours_Output')}>
          {sectionLoading.peakHours ? loadingPlaceholder : peakHours ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Peak Hour" value={peakHours.peak_hour != null ? `${String(peakHours.peak_hour).padStart(2,'0')}:00` : '—'} variant="purple" sub="Busiest hour" />
                <StatBox label="Peak Day" value={peakHours.peak_day || '—'} variant="blue" sub="Busiest day of week" />
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {peakHours.hourly_distribution?.length > 0 && (
                  <div id="kpi-chart-4" style={{ flex: 1, minWidth: 300 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#475569' }}>Trips by Hour of Day</div>
                    <ChartModeSelector
                      value={chartModes.kpi4Hourly}
                      onChange={(mode) => setChartMode('kpi4Hourly', mode)}
                      options={[
                        { value: 'bar', label: 'Bar' },
                        { value: 'area', label: 'Area' },
                      ]}
                    />
                    <ResponsiveContainer width="100%" height={220}>
                      {chartModes.kpi4Hourly === 'area' ? (
                        <AreaChart data={peakHours.hourly_distribution}>
                          <ChartGradients />
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" dataKey="trips" fill="url(#gradPrimary)" stroke={CHART_COLORS.primary} name="Total Trips" />
                          <Area type="monotone" dataKey="completed" fill="url(#gradSuccess)" stroke={CHART_COLORS.success} name="Completed" />
                        </AreaChart>
                      ) : (
                        <BarChart data={peakHours.hourly_distribution}>
                          <ChartGradients />
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="trips" fill="url(#gradPrimary)" stroke={CHART_COLORS.primary} strokeWidth={0} name="Total Trips" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="completed" fill="url(#gradSuccess)" stroke={CHART_COLORS.success} strokeWidth={0} name="Completed" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                )}
                {peakHours.daily_distribution?.length > 0 && (
                  <div style={{ flex: '0 0 320px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#475569' }}>Trips by Day of Week</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={peakHours.daily_distribution}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="day_name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="trips" fill="url(#gradAccent)" stroke={CHART_COLORS.accent} strokeWidth={0} name="Total Trips" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 5. Safe Driver Analysis ── */}
        <KpiSection
          title="5. Safe Driver Analysis"
          description="Driver behavior: speeding, violations, fines, overall driving safety"
          sourceSystems="Source: Samsara"
          exportMenu={secMenu(4, '5_Safe_Driver_Analysis')}
        >
          {sectionLoading.safety ? loadingPlaceholder : safety?.data_available ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Fleet Avg Score" value={safety.fleet_avg_safety_score} variant={safety.fleet_avg_safety_score >= 80 ? 'green' : safety.fleet_avg_safety_score >= 50 ? 'orange' : 'red'} sub="Safety rating" />
                <StatBox label="Drivers Scored" value={fmtNum(safety.driver_count)} variant="blue" />
              </div>
              {safety.drivers?.length > 0 && (
                <SortableTable
                  maxHeight={400}
                  columns={[
                    { key: 'driver_name', label: 'Driver' },
                    { key: 'avg_safety_score', label: 'Safety Score', sortValue: r => Number(r.avg_safety_score || 0), tdStyle: (r) => ({ fontWeight: 600, color: r.avg_safety_score >= 80 ? '#166534' : r.avg_safety_score >= 50 ? '#854d0e' : '#b91c1c' }) },
                    { key: 'harsh_events', label: 'Harsh Events', sortValue: r => Number(r.harsh_events || 0) },
                    { key: 'harsh_braking', label: 'Braking', sortValue: r => Number(r.harsh_braking || 0) },
                    { key: 'harsh_accel', label: 'Accel', sortValue: r => Number(r.harsh_accel || 0) },
                    { key: 'harsh_turning', label: 'Turning', sortValue: r => Number(r.harsh_turning || 0) },
                    { key: 'crashes', label: 'Crashes', sortValue: r => Number(r.crashes || 0) },
                    { key: 'speeding_minutes', label: 'Speeding (min)', sortValue: r => Number(r.speeding_minutes || 0) },
                  ]}
                  data={safety.drivers}
                />
              )}
            </>
          ) : (
            <div style={{ padding: 20, color: '#854d0e', background: '#fef9c3', borderRadius: 8 }}>
              {safety?.message || 'Safety scores data not yet available. Run migration m008 and load safety data from Samsara.'}
            </div>
          )}
        </KpiSection>

        {/* ── 6. Paid vs Loaded / Empty Time ── */}
        <KpiSection title="6. Paid vs Loaded / Empty Time Utilization (%)" description="Paid time vs productive loaded route-time and empty time" sourceSystems="Source: Paylocity + RouteGenie" exportMenu={secMenu(5, '6_Paid_vs_Loaded_Time')}>
          {sectionLoading.paidLoaded ? loadingPlaceholder : paidLoaded ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Paid Hours" value={fmtNum(paidLoaded.paid_hours)} sub={`${paidLoaded.employee_count} employees`} variant="blue" />
                <StatBox label="Loaded Hours" value={fmtNum(paidLoaded.loaded_hours ?? paidLoaded.active_hours)} sub="RouteGenie route duration" variant="green" />
                <StatBox label="Empty Hours" value={fmtNum(paidLoaded.empty_hours ?? paidLoaded.idle_hours)} sub="Paid minus loaded" variant="orange" />
                <StatBox label="Utilization" value={`${paidLoaded.utilization_pct}%`} sub="Loaded / Paid" variant="green" />
                <StatBox label="Routes" value={fmtNum(paidLoaded.route_count || 0)} variant="teal" />
              </div>
              {paidLoaded.data_gap_warning && (
                <div style={{ padding: 8, fontSize: 12, color: '#854d0e', background: '#fef9c3', borderRadius: 6, marginBottom: 12 }}>
                  {paidLoaded.data_gap_warning}
                </div>
              )}
              {paidLoaded.daily_trend?.length > 0 && (
                <div id="kpi-chart-6">
                  <ChartModeSelector
                    value={chartModes.kpi6}
                    onChange={(mode) => setChartMode('kpi6', mode)}
                    options={[
                      { value: 'bar', label: 'Bar' },
                      { value: 'area', label: 'Area' },
                    ]}
                  />
                  <ResponsiveContainer width="100%" height={260}>
                    {chartModes.kpi6 === 'area' ? (
                      <AreaChart data={paidLoadedTrend}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatDateTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                        <Area type="monotone" dataKey="paid" fill="url(#gradPrimary)" stroke={CHART_COLORS.primary} name="Paid Hours" />
                        <Area type="monotone" dataKey="active" fill="url(#gradSuccess)" stroke={CHART_COLORS.success} name="Loaded Hours" />
                        <Area type="monotone" dataKey="idle" fill="url(#gradWarning)" stroke={CHART_COLORS.warning} name="Empty Hours" />
                      </AreaChart>
                    ) : (
                      <BarChart data={paidLoadedTrend}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatDateTick} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#64748b' }} />
                        <Bar dataKey="paid" fill="url(#gradPrimary)" stroke={CHART_COLORS.primary} strokeWidth={0} name="Paid Hours" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="active" fill="url(#gradSuccess)" stroke={CHART_COLORS.success} strokeWidth={0} name="Loaded Hours" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="idle" fill="url(#gradWarning)" stroke={CHART_COLORS.warning} strokeWidth={0} name="Empty Hours" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              )}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 7. Profit per Driver ── */}
        <KpiSection title="7. Profit per Driver" description="Driver-level profitability contribution (cost breakdown)" sourceSystems="Source: RouteGenie + Paylocity + WEX" exportMenu={secMenu(6, '7_Profit_per_Driver')}>
          {sectionLoading.profitDriver ? loadingPlaceholder : displayedProfitDriver ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Total Revenue" value={fmtDollar(displayedProfitDriver.total_revenue)} variant="green" sub="Gross income" />
                <StatBox label="Total Profit" value={fmtDollar(displayedProfitDriver.total_profit)} variant={displayedProfitDriver.total_profit >= 0 ? 'green' : 'red'} sub="Revenue − costs" />
                <StatBox label="Overall Margin" value={displayedProfitDriver.overall_margin_pct != null ? `${displayedProfitDriver.overall_margin_pct}%` : '—'} variant={displayedProfitDriver.overall_margin_pct >= 0 ? 'green' : 'red'} sub="Profit margin" />
                <StatBox label="Overall ROI" value={displayedProfitDriver.overall_roi_pct != null ? `${displayedProfitDriver.overall_roi_pct}%` : '—'} variant={displayedProfitDriver.overall_roi_pct >= 0 ? 'green' : 'red'} sub="Return on investment" />
                <StatBox label="Total Labor Cost" value={fmtDollar(displayedProfitDriver.total_labor_cost)} variant="red" />
                <StatBox label="Total Fuel Cost" value={fmtDollar(displayedProfitDriver.total_fuel_cost)} variant="orange" />
                <StatBox label="Total Cost" value={fmtDollar(displayedProfitDriver.total_cost)} variant="red" sub="All expenses" />
                <StatBox label="Drivers" value={fmtNum(displayedProfitDriver.driver_count)} variant="blue" />
              </div>
              {displayedProfitDriver.note && (
                <div style={{ padding: 8, fontSize: 12, color: '#854d0e', background: '#fef9c3', borderRadius: 6, marginBottom: 12 }}>
                  {displayedProfitDriver.note}
                </div>
              )}
              {displayedProfitDriver.drivers?.length > 0 && (() => {
                const filteredProfitDrivers = displayedProfitDriver.drivers.filter(
                  d => matchesDepartment(d.department) && matchesDriverSearch(d.name)
                );
                return (
                <SortableTable
                  maxHeight={400}
                  columns={[
                    { key: 'name', label: 'Driver' },
                    { key: 'department', label: 'Department', tdStyle: () => ({ fontSize: 12 }) },
                    { key: 'paid_hours', label: 'Paid Hours', sortValue: r => Number(r.paid_hours || 0) },
                    { key: 'base_rate', label: 'Rate', render: r => fmtDollar(r.base_rate), sortValue: r => Number(r.base_rate || 0) },
                    { key: 'labor_cost', label: 'Labor Cost', render: r => fmtDollar(r.labor_cost), sortValue: r => Number(r.labor_cost || 0) },
                    { key: 'fuel_cost', label: 'Fuel Cost', render: r => fmtDollar(r.fuel_cost), sortValue: r => Number(r.fuel_cost || 0) },
                    { key: 'total_cost', label: 'Total Cost', render: r => fmtDollar(r.total_cost), sortValue: r => Number(r.total_cost || 0) },
                    { key: 'revenue', label: 'Revenue', render: r => fmtDollar(r.revenue), sortValue: r => Number(r.revenue || 0) },
                    { key: 'profit', label: 'Profit', render: r => fmtDollar(r.profit), sortValue: r => Number(r.profit || 0), tdStyle: r => ({ fontWeight: 600, color: r.profit >= 0 ? '#16a34a' : '#dc2626' }) },
                    { key: 'profit_margin_pct', label: 'Margin %', render: r => r.profit_margin_pct != null ? `${r.profit_margin_pct}%` : '—', sortValue: r => Number(r.profit_margin_pct || 0), tdStyle: r => ({ color: r.profit_margin_pct >= 0 ? '#16a34a' : '#dc2626' }) },
                    { key: 'roi_pct', label: 'ROI %', render: r => r.roi_pct != null ? `${r.roi_pct}%` : '—', sortValue: r => Number(r.roi_pct || 0), tdStyle: r => ({ color: r.roi_pct >= 0 ? '#16a34a' : '#dc2626' }) },
                  ]}
                  data={filteredProfitDrivers}
                />
                );
              })()}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 8. Profit per Trip ── */}
        <KpiSection title="8. Profit per Trip" description="Trip-level profitability contribution" sourceSystems="Source: RouteGenie + Paylocity + WEX" exportMenu={secMenu(7, '8_Profit_per_Trip')}>
          {sectionLoading.profitTrip ? loadingPlaceholder : profitTrip ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Total Revenue" value={fmtDollar(profitTrip.total_revenue)} variant="green" sub="Gross income" />
                <StatBox label="Total Profit" value={fmtDollar(profitTrip.total_profit)} variant={profitTrip.total_profit >= 0 ? 'green' : 'red'} sub="Net earnings" />
                <StatBox label="Margin %" value={profitTrip.profit_margin_pct != null ? `${profitTrip.profit_margin_pct}%` : '—'} variant={profitTrip.profit_margin_pct >= 0 ? 'green' : 'red'} sub="Profit margin" />
                <StatBox label="ROI %" value={profitTrip.roi_pct != null ? `${profitTrip.roi_pct}%` : '—'} variant={profitTrip.roi_pct >= 0 ? 'green' : 'red'} sub="Return on investment" />
                <StatBox label="Completed Trips" value={fmtNum(profitTrip.completed_trips)} variant="blue" />
                <StatBox label="Fuel Cost (Total)" value={fmtDollar(profitTrip.fuel_cost)} variant="orange" />
                <StatBox label="Total Cost" value={fmtDollar(profitTrip.total_cost)} variant="red" sub="All expenses" />
                <StatBox label="Revenue / Trip" value={fmtDollar(profitTrip.revenue_per_trip)} variant="green" />
                <StatBox label="Cost per Trip" value={fmtDollar(profitTrip.cost_per_trip)} variant="orange" />
                <StatBox label="Profit / Trip" value={fmtDollar(profitTrip.profit_per_trip)} variant={profitTrip.profit_per_trip >= 0 ? 'green' : 'red'} />
                <StatBox label="Labor / Trip" value={fmtDollar(profitTrip.labor_per_trip)} variant="red" />
                <StatBox label="Fuel / Trip" value={fmtDollar(profitTrip.fuel_per_trip)} variant="orange" />
              </div>
              {!profitTrip.revenue_available && (
                <div style={{ padding: 8, fontSize: 12, color: '#854d0e', background: '#fef9c3', borderRadius: 6, marginBottom: 12 }}>
                  {profitTrip.note}
                </div>
              )}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 9. Overall ROI ── */}
        <KpiSection title="9. Overall ROI (Return on Investment)" description="High-level organizational return: costs versus revenue" sourceSystems="Source: RouteGenie + Paylocity + WEX" exportMenu={secMenu(8, '9_Overall_ROI')}>
          {sectionLoading.roi ? loadingPlaceholder : roi ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Total Cost" value={fmtDollar(roi.total_cost)} variant="red" sub="All expenses" />
                <StatBox label="Labor Cost" value={fmtDollar(roi.labor_cost)} sub={`${roi.labor_pct}% of total`} variant="red" />
                <StatBox label="Fuel Cost" value={fmtDollar(roi.fuel_cost)} sub={`${roi.fuel_pct}% of total`} variant="orange" />
                <StatBox label="Cost / Trip" value={fmtDollar(roi.cost_per_trip)} variant="orange" sub="Per trip cost" />
                <StatBox label="Cost / Mile" value={fmtDollar(roi.cost_per_mile)} variant="orange" sub="Per mile cost" />
              </div>
              <div className="kpi-stats-row">
                <StatBox label="Completed Trips" value={fmtNum(roi.completed_trips)} sub={`of ${fmtNum(roi.total_trips)} total`} variant="green" />
                <StatBox label="Total Miles" value={fmtNum(roi.total_miles)} variant="blue" />
                <StatBox label="Paid Hours" value={fmtNum(roi.paid_hours)} sub={`OT: ${fmtNum(roi.ot_hours)}h`} variant="purple" />
                <StatBox label="Employees" value={fmtNum(roi.employees)} variant="teal" />
                <StatBox label="Fuel Gallons" value={fmtNum(roi.gallons)} variant="orange" />
              </div>
              {!roi.revenue_available && (
                <div style={{ padding: 8, fontSize: 12, color: '#854d0e', background: '#fef9c3', borderRadius: 6, marginBottom: 12 }}>
                  {roi.note}
                </div>
              )}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>

        {/* ── 10. Dispatcher Activity ── */}
        <KpiSection title="10. Dispatcher Activity / Dispatcher Productivity" description="Trips dispatched by each dispatcher — workload and effectiveness" sourceSystems="Source: RouteGenie + Paylocity" exportMenu={secMenu(9, '10_Dispatcher_Activity')}>
          {sectionLoading.dispatcher ? loadingPlaceholder : dispatcher ? (
            <>
              <div className="kpi-stats-row">
                <StatBox label="Dispatchers" value={fmtNum(dispatcher.dispatcher_count)} variant="blue" />
                <StatBox label="Dispatch Hours" value={fmtNum(dispatcher.total_dispatch_hours)} variant="purple" />
                <StatBox label="Trips Managed" value={fmtNum(dispatcher.total_trips_managed)} variant="teal" />
                <StatBox label="Trips / Dispatch Hr" value={dispatcher.trips_per_dispatch_hour} variant="green" sub="Productivity" />
                <StatBox label="Completed" value={fmtNum(dispatcher.completed_trips)} variant="green" />
                <StatBox label="Canceled" value={fmtNum(dispatcher.canceled_trips)} variant="red" />
              </div>
              {dispatcher.note && (
                <div style={{ padding: 8, fontSize: 12, color: '#854d0e', background: '#fef9c3', borderRadius: 6, marginBottom: 12 }}>
                  {dispatcher.note}
                </div>
              )}
              {dispatcher.dispatchers?.length > 0 && (
                <SortableTable
                  maxHeight={380}
                  columns={[
                    { key: 'name', label: 'Dispatcher' },
                    { key: 'paid_hours', label: 'Paid Hours', sortValue: r => Number(r.paid_hours || 0) },
                    { key: 'regular_hours', label: 'Regular Hours', sortValue: r => Number(r.regular_hours || 0) },
                    { key: 'ot_hours', label: 'OT Hours', sortValue: r => Number(r.ot_hours || 0) },
                    { key: 'shifts', label: 'Shifts', sortValue: r => Number(r.shifts || 0) },
                  ]}
                  data={dispatcher.dispatchers}
                />
              )}
            </>
          ) : <div style={{ color: '#94a3b8', padding: 20 }}>No data available</div>}
        </KpiSection>
        </>
        )}

        <button
          type="button"
          onClick={() => setAiOpen((v) => !v)}
          aria-label="Toggle AI Assistant"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1200,
            border: 'none',
            borderRadius: 999,
            padding: '12px 16px',
            background: '#2563eb',
            color: '#fff',
            fontWeight: 700,
            boxShadow: '0 10px 30px rgba(37,99,235,0.35)',
            cursor: 'pointer',
          }}
        >
          {aiOpen ? 'Close AI' : 'AI Assistant'}
        </button>

        {aiOpen && (
          <div
            style={{
              position: 'fixed',
              right: 24,
              bottom: 84,
              width: 'min(380px, calc(100vw - 32px))',
              maxHeight: '70vh',
              zIndex: 1201,
              background: '#fff',
              border: '1px solid #dbe4f0',
              borderRadius: 14,
              boxShadow: '0 20px 60px rgba(15,23,42,0.22)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
                fontWeight: 700,
                color: '#0f172a',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>SOL AI Assistant</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>Live KPI context</span>
            </div>

            <div
              ref={aiMessagesRef}
              style={{
                padding: 12,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                minHeight: 180,
              }}
            >
              {aiMessages.map((m, i) => {
                const isUser = m.role === 'user';
                return (
                  <div
                    key={`${m.role}-${i}`}
                    style={{
                      alignSelf: isUser ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: isUser ? '#2563eb' : '#f1f5f9',
                      color: isUser ? '#fff' : '#0f172a',
                      fontSize: 13,
                      lineHeight: 1.4,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {m.text}
                  </div>
                );
              })}
              {aiBusy && (
                <div
                  style={{
                    alignSelf: 'flex-start',
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: '#f1f5f9',
                    color: '#334155',
                    fontSize: 13,
                  }}
                >
                  Thinking...
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', padding: 10, display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    sendAiMessage();
                  }
                }}
                placeholder="Ask about trends, payer, profit, on-time..."
                style={{
                  flex: 1,
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <button
                type="button"
                onClick={sendAiMessage}
                disabled={aiBusy || !String(aiInput || '').trim()}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 14px',
                  background: aiBusy ? '#93c5fd' : '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: aiBusy ? 'not-allowed' : 'pointer',
                }}
              >
                Send
              </button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default KpiDashboard;
